import { type CustomEvent, CustomEventEmitter } from '@/package';

interface QueueItem<A extends (...args: any[]) => Promise<any>> {
  key: string;
  action: A;
  onSuccess: (result: Awaited<ReturnType<A>>) => void;
  onFail?: (err: Error) => void;
}

const createAsyncProcessQueue = <
  A extends (...args: any[]) => Promise<any>,
>(options?: {
  concurrency?: number;
}) => {
  const { concurrency = 2 } = options ?? {};

  let activeTaskCount = 0;
  let isRunning = false;
  let queue: QueueItem<A>[] = [];

  const event = new CustomEventEmitter<{
    complete: CustomEvent<'complete', 'complete', null>;
    release: CustomEvent<
      'release',
      'release',
      { key: string; result: any; error: Error | null }
    >;
  }>();

  const processNext = () => {
    if (!isRunning || activeTaskCount >= concurrency) {
      return;
    }

    if (isEmptyQueue()) {
      if (activeTaskCount === 0) {
        isRunning = false;
        event.emit('complete');
      }

      return;
    }

    activeTaskCount++;
    const item = queue.shift();

    if (!item) {
      activeTaskCount--;
      return;
    }

    const { key, action, onSuccess, onFail } = item;
    const data = {
      key,
      result: null as any,
      error: null as Error | null,
    };

    action()
      .then(res => {
        data.result = res;
        onSuccess(res);
      })
      .catch(err => {
        data.error = err;
        onFail?.(err);
      })
      .finally(() => {
        activeTaskCount--;

        event.emit('release', data);

        processNext();
      });
  };

  const clearQueue = () => {
    isRunning = false;
    queue = [];
    activeTaskCount = 0;

    event.disposeEvent();
  };

  const getQueueSize = () => queue.length;

  const isEmptyQueue = () => getQueueSize() === 0;

  const pause = () => {
    isRunning = false;
  };

  const push = (item: QueueItem<A>, autoRun: boolean = true) => {
    queue.push(item);

    if (!autoRun) return;

    if (!isRunning) {
      run();
    } else {
      processNext();
    }
  };

  const run = () => {
    if (isRunning) {
      console.warn('이미 실행 중인 비동기 큐입니다.');
      return;
    }

    isRunning = true;

    for (let i = 0; i < concurrency; i++) {
      processNext();
    }
  };

  return {
    event,

    clearQueue,
    getQueueSize,
    isEmptyQueue,
    pause,
    push,
    run,
  };
};

export default createAsyncProcessQueue;
